<template>
  <div class="booking-wrap">

    <!-- 뒤로가기 -->
    <div class="bk-header">
      <button @click="$emit('booking-failed')" class="btn-back-top">← 검색으로 돌아가기</button>
      <div class="bk-hotel-title">{{ hotelInfo.name }}</div>
    </div>

    <!-- 단계 표시 -->
    <div class="step-bar">
      <div class="step" :class="{ active: step >= 1, done: step > 1 }">
        <span class="step-num">1</span>
        <span class="step-label">정책 확인</span>
      </div>
      <div class="step-line" :class="{ done: step > 1 }"></div>
      <div class="step" :class="{ active: step >= 2, done: step > 2 }">
        <span class="step-num">2</span>
        <span class="step-label">투숙객 입력</span>
      </div>
      <div class="step-line" :class="{ done: step > 2 }"></div>
      <div class="step" :class="{ active: step >= 3 }">
        <span class="step-num">3</span>
        <span class="step-label">예약 확정</span>
      </div>
    </div>

    <!-- 로딩 -->
    <div v-if="loading" class="bk-loading">
      <div class="bk-spinner"></div>
      <p>{{ loadingMsg }}</p>
    </div>

    <!-- ── STEP 1: 정책 확인 ── -->
    <div v-else-if="step === 1" class="bk-section">
      <h2 class="bk-title">예약 정책 확인</h2>

      <!-- 호텔 요약 -->
      <div class="hotel-summary">
        <div class="hs-name">{{ hotelInfo.name }}</div>
        <div class="hs-detail">
          📅 {{ searchData.check_in_date }} ~ {{ searchData.check_out_date }}
          ({{ searchData.night_count }}박)
          &nbsp;·&nbsp;
          👥 성인 {{ searchData.adult_count }}명 / {{ searchData.room_count }}실
        </div>
      </div>

      <!-- 가격 정보 -->
      <div class="price-summary">
        <div class="ps-row">
          <span>제공가 (매입가)=>고객표시X</span>
          <span>{{ krw(searchData.cost_krw) }}원</span>
        </div>
        <div class="ps-row ps-selling">
          <span>판매가</span>
          <span class="ps-price">{{ krw(searchData.selling_krw) }}원</span>
        </div>
        <div v-if="policyPriceChanged" class="ps-row ps-warn">
          <span>⚠️ 가격 변동 감지 (USD)</span>
          <span>
            {{ searchData.rtxPackage.room_rate }}
            →
            {{ policyData.package.room_rate }}
            {{ policyData.package.room_rate_currency }}
          </span>
        </div>
      </div>

      <!-- 취소 정책 -->
      <div v-if="policyData?.cancellation_policy" class="cancel-policy">
        <div class="cp-title">📋 취소 정책</div>
        <div v-if="policyData.cancellation_policy.remarks" class="cp-remarks">
          {{ policyData.cancellation_policy.remarks }}
        </div>
        <div v-for="(band, i) in policyData.cancellation_policy.cancellation_policies"
            :key="i" class="cp-band">
          <span class="cp-pct" :class="band.penalty_percentage === 0 ? 'pct-free' : 'pct-paid'">
            {{ band.penalty_percentage === 0 ? '무료 취소' : `위약금 ${band.penalty_percentage}%` }}
          </span>
          <span class="cp-date">
            {{ formatDate(band.date_from) }} ~ {{ formatDate(band.date_to) }}
          </span>
        </div>
      </div>

      <!-- 정책 로드 실패 시 -->
      <div v-else-if="!policyData && !loading" class="policy-warn">
        ⚠️ 예약 정책을 불러오지 못했습니다.
        <button @click="fetchBookingPolicy" class="btn-retry">다시 시도</button>
      </div>

      <button @click="goToStep2" :disabled="!bookingPolicyId" class="btn-next">
        다음 → 투숙객 입력
      </button>
    </div>


    <!-- ── STEP 2: 투숙객 입력 ── -->
    <div v-else-if="step === 2" class="bk-section">
      <h2 class="bk-title">투숙객 정보 입력</h2>

      <!-- ① 예약자 정보 -->
      <div class="guest-block block-contact">
        <div class="gb-title">👤 예약자 정보</div>
        <div class="gb-row">
          <input v-model="guestForm.contact_person.first_name" placeholder="영문 이름 (First)" class="gb-input" />
          <input v-model="guestForm.contact_person.last_name"  placeholder="영문 성 (Last)"   class="gb-input" />
        </div>
        <div class="gb-row">
          <input v-model="guestForm.contact_person.contact_no" placeholder="연락처 (예: 01012345678)" class="gb-input" />
          <input v-model="guestForm.contact_person.email" placeholder="이메일 (선택)" class="gb-input" type="email" />
        </div>
      </div>

      <!-- ② 예약자 = 투숙객 여부 (1실일 때만 표시) -->
      <div v-if="searchData.room_count === 1" class="same-guest-toggle">
        <label class="sgt-label">
          <input type="radio" v-model="sameAsContact" :value="true"  @change="onSameToggle" />
          예약자와 투숙객이 동일합니다
        </label>
        <label class="sgt-label">
          <input type="radio" v-model="sameAsContact" :value="false" @change="onSameToggle" />
          예약자와 투숙객이 다릅니다 (대리 예약)
        </label>
      </div>

      <!-- ③ 객실별 투숙객 -->
      <div v-for="(room, ri) in guestForm.room_lead_guests" :key="ri" class="room-block">

        <!-- 객실 헤더 -->
        <div class="room-block-header">
          <span class="room-block-label">🛏 {{ ri + 1 }}호실</span>
          <span class="room-block-sub">대표 투숙객 (필수)</span>
        </div>

        <!-- 대표 투숙객 -->
        <div class="gb-row">
          <select v-model="room.salutation" class="gb-select">
            <option value="">호칭</option>
            <option value="Mr">Mr</option>
            <option value="Mrs">Mrs</option>
            <option value="Ms">Ms</option>
            <option value="Dr">Dr</option>
          </select>
          <input v-model="room.first_name" placeholder="영문 이름 (First) *" class="gb-input" :disabled="ri === 0 && sameAsContact" />
          <input v-model="room.last_name" placeholder="영문 성 (Last) *" class="gb-input" :disabled="ri === 0 && sameAsContact" />
        </div>
        <div class="gb-row">
          <input v-model="room.nationality" placeholder="국적 코드 * (예: KR, JP, US)" class="gb-input" maxlength="2" style="text-transform:uppercase;" :disabled="ri === 0 && sameAsContact" />
        </div>

        <!-- 추가 투숙객 목록 : api 미지원 -->
        <!-- <div v-if="room.additional_guests.length" class="add-guest-list">
          <div class="add-guest-header">추가 투숙객</div>
          <div v-for="(ag, ai) in room.additional_guests" :key="ai" class="add-guest-row">
            <select v-model="ag.salutation" class="gb-select gb-select-sm">
              <option value="">호칭</option>
              <option value="Mr">Mr</option>
              <option value="Mrs">Mrs</option>
              <option value="Ms">Ms</option>
              <option value="Dr">Dr</option>
            </select>
            <input v-model="ag.first_name" placeholder="영문 이름" class="gb-input" />
            <input v-model="ag.last_name"  placeholder="영문 성"   class="gb-input" />
            <button class="btn-remove-guest" @click="removeAdditionalGuest(ri, ai)">✕</button>
          </div>
        </div> -->

        <!-- 추가 투숙객 추가 버튼 -->
        <!-- <button class="btn-add-guest" @click="addAdditionalGuest(ri)">
          + 추가 투숙객 입력 (선택)
        </button> -->

      </div>

      <!-- ④ 예상 도착 시간 -->
      <!-- <div class="guest-block">
        <div class="gb-title">🕐 예상 도착 시간 <span class="optional-label">(선택)</span></div>
        <div class="gb-row">
          <select v-model="guestForm.estimated_arrival_time" class="gb-input">
            <option value="">미정</option>
            <option v-for="t in arrivalTimeOptions" :key="t.value" :value="t.value">
              {{ t.label }}
            </option>
          </select>
        </div>
        <div class="gb-hint">체크인 예정 시각을 알려주시면 호텔에 전달됩니다.</div>
      </div> -->

      <!-- ⑤ 특별 전달 사항 -->
      <div class="guest-block">
        <div class="gb-title">📝 특별 전달 사항 <span class="optional-label">(선택)</span></div>
        <textarea
          v-model="guestForm.special_requests"
          class="gb-textarea"
          placeholder="호텔에 전달할 특별 요청사항을 입력해주세요.&#10;예) 금연 객실 요청, 높은 층 선호, 조용한 객실 희망 등&#10;※ 요청사항은 보장되지 않으며, 추가비용이 발생할 수 있습니다."
          rows="4"
        />
      </div>

      <div v-if="formErr" class="form-err">⚠️ {{ formErr }}</div>

      <div class="btn-row">
        <button @click="step = 1" class="btn-back">← 이전</button>
        <button @click="doPreBook" class="btn-next">예약 신청 →</button>
      </div>
    </div>


    <!-- ── STEP 3: 예약 확정 대기 ── -->
    <div v-else-if="step === 3" class="bk-section">
      <h2 class="bk-title">예약 처리 중</h2>

      <div class="status-box" :class="statusClass">
        <div class="status-icon">{{ statusIcon }}</div>
        <div class="status-text">{{ statusText }}</div>
        <div v-if="bookingId" class="status-id">예약 ID: {{ bookingId }}</div>
        <div v-if="bookStatus === 'bkg-ps'" class="status-sub">
          공급사 확인 중입니다. 수 분에서 수 시간 소요될 수 있습니다.
        </div>
      </div>

      <!-- 확정 완료 -->
      <div v-if="bookStatus === 'bkg-cf'" class="result-box result-success">
        <p>🎉 예약이 확정되었습니다!</p>
        <div class="result-info">
          <div class="ri-row">
            <span>예약번호</span>
            <strong>{{ bookingNo }}</strong>
          </div>
          <div class="ri-row" style="font-size:11px; color:#999;">
            <span>RTX ID</span>
            <span>{{ bookingId }}</span>
          </div>
          <div class="ri-row"><span>호텔</span><strong>{{ hotelInfo.name }}</strong></div>
          <div class="ri-row"><span>체크인</span><strong>{{ searchData.check_in_date }}</strong></div>
          <div class="ri-row"><span>체크아웃</span><strong>{{ searchData.check_out_date }}</strong></div>
        </div>
        <button @click="doCancel" class="btn-cancel">예약 취소</button>
        <button @click="$emit('booking-complete', bookingId)" class="btn-next" style="margin-top:8px;">
          완료 →
        </button>
      </div>

      <!-- 실패 -->
      <div v-if="bookStatus === 'bkg-af'" class="result-box result-fail">
        <p>❌ 예약에 실패했습니다.</p>
        <p class="fail-desc">다른 객실을 선택하거나 다시 검색해주세요.</p>
        <button @click="$emit('booking-failed')" class="btn-back">← 다시 검색</button>
      </div>

      <!-- 취소 완료 -->
      <div v-if="bookStatus === 'bkg-cx'" class="result-box result-cancel">
        <p>🚫 예약이 취소되었습니다.</p>
        <button @click="$emit('booking-failed')" class="btn-back">← 검색으로 돌아가기</button>
      </div>

    </div>

  </div>
</template>


<script>
export default {
  name: 'RtxBooking',

  props: {
    hotelInfo:  { type: Object, required: true },
    searchData: { type: Object, required: true }
  },

  emits: ['booking-complete', 'booking-failed'],

  data() {
    return {
      step:       1,
      loading:    false,
      loadingMsg: '',

      policyData:       null,
      bookingPolicyId:  null,
      bookingId:        null,
      bookingNo:        null,
      bookStatus:       null,

      sameAsContact: false,   // 예약자 = 대표투숙객 여부

      guestForm: {
        room_lead_guests: [],
        contact_person: {
          first_name:  '',
          last_name:   '',
          contact_no:  '',
          email:       '',
          nationality: 'KR',
        },
        special_requests: ''
      },

      // // 도착 시간 선택지
      // arrivalTimeOptions: [
      //   { value: '12:00', label: '오후 12:00 (정오)' },
      //   { value: '13:00', label: '오후 1:00' },
      //   { value: '14:00', label: '오후 2:00 (일반 체크인)' },
      //   { value: '15:00', label: '오후 3:00' },
      //   { value: '16:00', label: '오후 4:00' },
      //   { value: '17:00', label: '오후 5:00' },
      //   { value: '18:00', label: '오후 6:00' },
      //   { value: '19:00', label: '오후 7:00' },
      //   { value: '20:00', label: '오후 8:00' },
      //   { value: '21:00', label: '오후 9:00' },
      //   { value: '22:00', label: '오후 10:00' },
      //   { value: '23:00', label: '오후 11:00' },
      //   { value: '00:00', label: '자정 이후 (익일 0시)' },
      // ],

      formErr:   '',
      pollTimer: null,
      pollCount: 0,
      pollPhase: 1
    };
  },

  computed: {
    user() { return this.$store.state.user; },

    policyPriceChanged() {
      const oldRate = this.searchData?.rtxPackage?.room_rate;
      const newRate = this.policyData?.package?.room_rate;
      if (oldRate == null || newRate == null) return false;
      return oldRate !== newRate;
    },

    statusClass() {
      if (this.bookStatus === 'bkg-cf') return 'status-success';
      if (this.bookStatus === 'bkg-af') return 'status-fail';
      if (this.bookStatus === 'bkg-cx') return 'status-cancel';
      return 'status-pending';
    },

    statusIcon() {
      if (this.bookStatus === 'bkg-cf') return '✅';
      if (this.bookStatus === 'bkg-af') return '❌';
      if (this.bookStatus === 'bkg-cx') return '🚫';
      return '⏳';
    },

    statusText() {
      const map = {
        'bkg-ns': '예약 준비 중...',
        'bkg-ip': '예약 처리 중...',
        'bkg-ps': '공급사 확인 대기 중...',
        'bkg-cf': '예약 확정!',
        'bkg-af': '예약 실패',
        'bkg-cx': '예약 취소됨'
      };
      return map[this.bookStatus] || '처리 중...';
    }
  },

  async created() {
    const roomCount = this.searchData.room_count || 1;

    // 객실 수만큼 투숙객 폼 초기화 (추가투숙객 배열 포함)
    this.guestForm.room_lead_guests = Array.from({ length: roomCount }, () => ({
      salutation:        '',
      first_name:        '',
      last_name:         '',
      nationality:       'KR',
      // additional_guests: []   // ← 추가 투숙객(api에서 지원X)
    }));

    await this.fetchBookingPolicy();
  },

  beforeUnmount() {
    this.stopPoll();
  },

  methods: {

    // ── 예약자 = 투숙객 토글 ─────────────────────────
    onSameToggle() {
      const lead = this.guestForm.room_lead_guests[0];
      if (this.sameAsContact) {
        const c = this.guestForm.contact_person;
        lead.first_name  = c.first_name;
        lead.last_name   = c.last_name;
        lead.nationality = c.nationality || 'KR';
      } else {
        lead.first_name  = '';
        lead.last_name   = '';
        lead.nationality = 'KR';
      }
    },

    // ── 추가 투숙객 추가/삭제 : api미지원 ────────────────────────
    // addAdditionalGuest(roomIdx) {
    //   this.guestForm.room_lead_guests[roomIdx].additional_guests.push({
    //     salutation: '',
    //     first_name: '',
    //     last_name:  ''
    //   });
    // },

    // removeAdditionalGuest(roomIdx, guestIdx) {
    //   this.guestForm.room_lead_guests[roomIdx].additional_guests.splice(guestIdx, 1);
    // },

    // ① PostBookingPolicy
    async fetchBookingPolicy() {
      this.loading    = true;
      this.loadingMsg = '예약 정책을 불러오는 중...';
      try {
        const result = await this.$api('/api/rtx/postBookingPolicy', {
          param: [{
            session_id: this.searchData.session_id,
            search:     this.searchData.rtxSearch,
            package:    this.searchData.rtxPackage
          }]
        });

        if (result.success) {
          this.policyData      = result.data;
          this.bookingPolicyId = result.booking_policy_id;
        } else {
          throw new Error(result.message || '정책 조회 실패');
        }
      } catch (e) {
        const msg = e?.response?.data?.message || e?.message || '정책 조회 중 오류가 발생했습니다.';
        console.error('[RTX] BookingPolicy 오류:', e);
        this.$swal({ icon: 'error', title: '정책 조회 실패', text: msg });
      } finally {
        this.loading = false;
      }
    },

    // STEP 1 → 2
    goToStep2() {
      if (!this.bookingPolicyId) {
        this.$swal({ icon: 'warning', text: '예약 정책을 먼저 불러와주세요.' });
        return;
      }
      if (this.policyPriceChanged) {
        this.$swal({
          icon:  'question',
          title: '가격이 변동되었습니다',
          html:  `죄송합니다.<br>호텔 측에서 <span class="text-danger">요금이 변경</span>되어 예약을 진행할 수 없습니다.<br>다시 검색해주세요.`,
          confirmButtonText: '검색으로 돌아가기'
        }).then(() => {
          this.$emit('booking-failed');
        });
        return;
      }
      this.step = 2;
    },

    // ② PostPreBook
    async doPreBook() {
      this.formErr = this.validateGuests();
      if (this.formErr) return;

      this.loading    = true;
      this.loadingMsg = '예약 정보를 등록하는 중...';
      
      try 
      {
        // additional_guests가 빈 배열이면 제거 (API 전송 최소화)
        const roomGuests = this.guestForm.room_lead_guests.map(r => ({
          salutation:  r.salutation,
          first_name:  r.first_name,
          last_name:   r.last_name,
          nationality: r.nationality,
          remarks:     this.guestForm.special_requests || ''  // 특별요청(선택)
          // ...(r.additional_guests.length && { additional_guests: r.additional_guests })    //추가투숙객 (api 미지원)
        }));

        const result = await this.$api('/api/rtx/postPreBook', {
          param: [{
            booking_policy_id:      this.bookingPolicyId,
            session_id:             this.searchData.session_id,
            room_lead_guests:       roomGuests,
            contact_person:         this.guestForm.contact_person,
            // estimated_arrival_time: this.guestForm.estimated_arrival_time || null,
            // special_requests:       this.guestForm.special_requests       || null,
          }]
        });

        if (result.success) 
        {
          this.bookingId = result.booking_id;
          this.bookingNo = result.booking_no;
          await this.doBook();
        } 
        else 
        {
          throw new Error(result.message || 'PreBook 실패');
        }
      } 
      catch (e) 
      {
        console.error('[RTX] PreBook 오류:', e);
        this.$swal({ icon: 'error', title: 'PreBook 실패', text: e.message });
      } 
      finally 
      {
        this.loading = false;
      }
    },

    // ③ PostBook
    async doBook() {
      this.loading    = true;
      this.loadingMsg = '예약을 생성하는 중...';
      this.step       = 3;
      try {
        const result = await this.$api('/api/rtx/postBook', {
          param: [{ booking_id: this.bookingId, session_id: this.searchData.session_id }]
        });
        if (result.success) {
          this.bookStatus = result.status;
          this.startPoll();
        } else {
          throw new Error(result.message || 'Book 실패');
        }
      } catch (e) {
        console.error('[RTX] Book 오류:', e);
        this.bookStatus = 'bkg-af';
      } finally {
        this.loading = false;
      }
    },

    // ④ GetBookStatus 폴링
    startPoll() {
      this.pollCount = 0;
      this.pollPhase = 1;

      const poll = async () => {
        if (!this.bookingId) return;
        this.pollCount++;

        try {
          const result = await this.$api('/api/rtx/getBookStatus', {
            param: [{ booking_id: this.bookingId, session_id: this.searchData.session_id }]
          });
          if (result.success) this.bookStatus = result.status;
        } catch (e) {
          console.error('[RTX] 폴링 오류:', e);
        }

        if (['bkg-cf', 'bkg-af', 'bkg-cx'].includes(this.bookStatus)) {
          this.stopPoll();
          return;
        }

        let nextInterval = 5000;
        if (this.pollCount > 12)     nextInterval = 60000;
        if (this.pollCount > 12 + 5) nextInterval = 300000;
        this.pollTimer = setTimeout(poll, nextInterval);
      };

      this.pollTimer = setTimeout(poll, 5000);
    },

    stopPoll() {
      if (this.pollTimer) { clearTimeout(this.pollTimer); this.pollTimer = null; }
    },

    // ⑤ PostCancel
    async doCancel() {
      const confirm = await this.$swal.fire({
        icon: 'warning', title: '예약을 취소하시겠습니까?',
        text: '취소 후 복구가 불가능합니다.',
        showCancelButton: true, confirmButtonText: '취소 진행', cancelButtonText: '닫기'
      });
      if (!confirm.isConfirmed) return;

      this.loading    = true;
      this.loadingMsg = '예약을 취소하는 중...';
      try {
        const result = await this.$api('/api/rtx/postCancel', {
          param: [{ booking_id: this.bookingId, session_id: this.searchData.session_id }]
        });
        if (result.success) { this.bookStatus = result.status; this.startPoll(); }
        else throw new Error(result.message || '취소 실패');
      } catch (e) {
        console.error('[RTX] 취소 오류:', e);
        this.$swal({ icon: 'error', title: '취소 실패', text: e.message });
      } finally {
        this.loading = false;
      }
    },

    // ── 유효성 검사 ──────────────────────────────────
    validateGuests() {
      // 예약자 정보
      const c = this.guestForm.contact_person;
      if (!c.first_name) return '예약자 이름(First)을 입력해주세요.';
      if (!c.last_name)  return '예약자 성(Last)을 입력해주세요.';
      if (!c.contact_no) return '예약자 연락처를 입력해주세요.';

      // 객실별 대표 투숙객
      for (let i = 0; i < this.guestForm.room_lead_guests.length; i++) {
        const g = this.guestForm.room_lead_guests[i];
        if (!g.first_name)  return `${i + 1}호실 대표 투숙객 이름(First)을 입력해주세요.`;
        if (!g.last_name)   return `${i + 1}호실 대표 투숙객 성(Last)을 입력해주세요.`;
        if (!g.nationality) return `${i + 1}호실 국적 코드를 입력해주세요. (예: KR)`;

        // 추가 투숙객 - 이름 입력 시 성도 필수
        for (let j = 0; j < g.additional_guests.length; j++) {
          const ag = g.additional_guests[j];
          if (ag.first_name && !ag.last_name) return `${i + 1}호실 추가투숙객 ${j + 1}번 성(Last)을 입력해주세요.`;
          if (!ag.first_name && ag.last_name) return `${i + 1}호실 추가투숙객 ${j + 1}번 이름(First)을 입력해주세요.`;
        }
      }
      return '';
    },

    // ── 유틸 ─────────────────────────────────────────
    krw(val) {
      if (val == null) return '-';
      return new Intl.NumberFormat('ko-KR').format(val);
    },

    formatDate(dateStr) {
      if (!dateStr) return '-';
      const d = new Date(dateStr);
      return `${d.getFullYear()}.${d.getMonth() + 1}.${d.getDate()}`;
    }
  }
};
</script>


<style scoped>
.booking-wrap {
  max-width: 680px; margin: 0 auto; padding: 24px 16px;
  font-family: 'Noto Sans KR', sans-serif;
}

/* 헤더 */
.bk-header { display: flex; align-items: center; gap: 16px; margin-bottom: 24px; }
.btn-back-top {
  background: none; border: 1px solid #ddd; border-radius: 8px;
  padding: 7px 14px; font-size: 13px; color: #555; cursor: pointer;
  white-space: nowrap; transition: all 0.15s;
}
.btn-back-top:hover { border-color: #1a6eb5; color: #1a6eb5; }
.bk-hotel-title { font-size: 16px; font-weight: 800; color: #1a1a2e; }

/* 단계 바 */
.step-bar { display: flex; align-items: center; justify-content: center; margin-bottom: 32px; }
.step { display: flex; flex-direction: column; align-items: center; gap: 4px; }
.step-num {
  width: 32px; height: 32px; border-radius: 50%;
  background: #e2e8f0; color: #999;
  display: flex; align-items: center; justify-content: center;
  font-size: 14px; font-weight: 700; transition: all 0.2s;
}
.step.active .step-num { background: #1a6eb5; color: #fff; }
.step.done   .step-num { background: #86cd53; color: #fff; }
.step-label { font-size: 11px; color: #999; white-space: nowrap; }
.step.active .step-label { color: #1a6eb5; font-weight: 700; }
.step-line { flex: 1; height: 2px; background: #e2e8f0; min-width: 48px; transition: background 0.2s; }
.step-line.done { background: #86cd53; }

/* 로딩 */
.bk-loading { text-align: center; padding: 60px 0; }
.bk-spinner {
  width: 44px; height: 44px; margin: 0 auto 16px;
  border: 4px solid #eee; border-top-color: #1a6eb5;
  border-radius: 50%; animation: spin 0.8s linear infinite;
}
.bk-loading p { color: #888; font-size: 14px; }

/* 섹션 */
.bk-section {
  background: #fff; border-radius: 14px; padding: 28px;
  box-shadow: 0 2px 16px rgba(0,0,0,0.08);
}
.bk-title { font-size: 18px; font-weight: 800; color: #1a1a2e; margin-bottom: 20px; }

/* 호텔 요약 */
.hotel-summary {
  background: #f8f9ff; border-radius: 10px; padding: 14px 16px; margin-bottom: 16px;
}
.hs-name   { font-size: 15px; font-weight: 800; color: #1a1a2e; margin-bottom: 5px; }
.hs-detail { font-size: 13px; color: #666; line-height: 1.6; }

/* 가격 요약 */
.price-summary {
  border: 1px solid #e2e8f0; border-radius: 10px; padding: 14px 16px; margin-bottom: 16px;
}
.ps-row { display: flex; justify-content: space-between; padding: 5px 0; font-size: 14px; color: #555; }
.ps-selling .ps-price { font-size: 22px; font-weight: 900; color: #1a1a2e; }
.ps-warn { color: #e53e3e; font-weight: 700; font-size: 13px; }

/* 취소 정책 */
.cancel-policy { border: 1px solid #e2e8f0; border-radius: 10px; padding: 14px 16px; margin-bottom: 20px; }
.cp-title   { font-size: 13px; font-weight: 700; color: #444; margin-bottom: 8px; }
.cp-remarks { font-size: 12px; color: #666; margin-bottom: 10px; line-height: 1.6; }
.cp-band    { display: flex; justify-content: space-between; align-items: center; padding: 4px 0; }
.cp-pct     { font-size: 13px; font-weight: 700; }
.pct-free   { color: #276749; }
.pct-paid   { color: #c53030; }
.cp-date    { font-size: 12px; color: #888; }

/* 정책 로드 실패 */
.policy-warn { text-align: center; color: #e53e3e; font-size: 14px; padding: 20px 0; }
.btn-retry   { margin-left: 12px; padding: 6px 14px; background: #f0f0f0; border: none; border-radius: 6px; cursor: pointer; font-size: 13px; }

/* 예약자 = 투숙객 토글 */
.same-guest-toggle {
  display: flex; flex-direction: column; gap: 8px;
  background: #f8f9ff; border-radius: 10px;
  padding: 14px 16px; margin-bottom: 14px;
  border: 1px solid #dce6ff;
}
.sgt-label {
  display: flex; align-items: center; gap: 8px;
  font-size: 14px; color: #444; cursor: pointer;
}
.sgt-label input[type="radio"] { accent-color: #1a6eb5; width: 16px; height: 16px; cursor: pointer; }

/* 투숙객 공통 블록 */
.guest-block {
  border: 1px solid #e2e8f0; border-radius: 10px;
  padding: 16px; margin-bottom: 14px;
}
.block-contact { border-color: #dce6ff; background: #fafbff; }

.gb-title {
  font-size: 13px; font-weight: 700; color: #444; margin-bottom: 10px;
  display: flex; align-items: center; gap: 6px;
}
.optional-label { font-size: 11px; font-weight: 400; color: #aaa; }
.gb-row   { display: flex; gap: 8px; margin-bottom: 8px; }
.gb-input {
  flex: 1; padding: 9px 12px; border: 1px solid #ddd;
  border-radius: 8px; font-size: 14px; outline: none; transition: border-color 0.15s;
}
.gb-input:focus  { border-color: #1a6eb5; }
.gb-input:disabled { background: #f5f5f5; color: #aaa; cursor: not-allowed; }
.gb-select {
  padding: 9px 12px; border: 1px solid #ddd;
  border-radius: 8px; font-size: 14px; width: 90px; outline: none;
}
.gb-hint { font-size: 12px; color: #999; margin-top: 4px; }

/* 텍스트에리어 */
.gb-textarea {
  width: 100%; padding: 10px 12px; border: 1px solid #ddd;
  border-radius: 8px; font-size: 13px; outline: none;
  resize: vertical; line-height: 1.6; box-sizing: border-box;
  font-family: 'Noto Sans KR', sans-serif; color: #444;
  transition: border-color 0.15s;
}
.gb-textarea:focus { border-color: #1a6eb5; }

/* 객실 블록 */
.room-block {
  border: 1px solid #e2e8f0; border-radius: 10px;
  padding: 16px; margin-bottom: 14px;
}
.room-block-header {
  display: flex; align-items: center; gap: 10px; margin-bottom: 12px;
}
.room-block-label {
  font-size: 14px; font-weight: 800; color: #1a6eb5;
}
.room-block-sub {
  font-size: 12px; color: #888; font-weight: 500;
}

/* 추가 투숙객 */
.add-guest-list  { margin-top: 12px; margin-bottom: 8px; }
.add-guest-header {
  font-size: 12px; font-weight: 700; color: #888;
  margin-bottom: 8px; padding-bottom: 4px;
  border-bottom: 1px dashed #e2e8f0;
}
.add-guest-row { display: flex; gap: 8px; margin-bottom: 8px; align-items: center; }
.gb-select-sm  { width: 76px; font-size: 13px; }
.btn-remove-guest {
  background: #fff5f5; border: 1px solid #fed7d7; color: #c53030;
  border-radius: 6px; width: 30px; height: 36px;
  font-size: 13px; cursor: pointer; flex-shrink: 0;
  transition: background 0.15s;
}
.btn-remove-guest:hover { background: #fed7d7; }

.btn-add-guest {
  background: none; border: 1px dashed #86cd53; color: #4a9e2a;
  border-radius: 8px; padding: 7px 14px;
  font-size: 13px; font-weight: 600; cursor: pointer;
  width: 100%; transition: all 0.15s; margin-top: 4px;
}
.btn-add-guest:hover { background: #f0fff0; }

.form-err {
  color: #e53e3e; font-size: 13px; margin-bottom: 12px;
  padding: 8px 12px; background: #fff5f5; border-radius: 8px;
}

/* 버튼 */
.btn-row { display: flex; gap: 10px; margin-top: 20px; }
.btn-next {
  flex: 1; padding: 13px; background: #1a6eb5; color: #fff;
  border: none; border-radius: 8px;
  font-size: 15px; font-weight: 700; cursor: pointer; transition: background 0.2s;
}
.btn-next:hover:not(:disabled)  { background: #86cd53; }
.btn-next:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-back {
  padding: 13px 20px; background: #f0f0f0; color: #555;
  border: none; border-radius: 8px;
  font-size: 14px; font-weight: 600; cursor: pointer; transition: background 0.15s;
}
.btn-back:hover { background: #e2e8f0; }

/* 상태 박스 */
.status-box { text-align: center; padding: 36px 20px; border-radius: 14px; margin-bottom: 20px; }
.status-pending { background: #fffbeb; border: 1px solid #fde68a; }
.status-success { background: #f0fff4; border: 1px solid #c6f6d5; }
.status-fail    { background: #fff5f5; border: 1px solid #fed7d7; }
.status-cancel  { background: #f7f7f7; border: 1px solid #e2e8f0; }
.status-icon    { font-size: 48px; margin-bottom: 12px; }
.status-text    { font-size: 18px; font-weight: 800; color: #1a1a2e; }
.status-id      { font-size: 12px; color: #888; margin-top: 6px; }
.status-sub     { font-size: 12px; color: #888; margin-top: 8px; line-height: 1.6; }

/* 결과 박스 */
.result-box { border-radius: 12px; padding: 24px; margin-top: 16px; text-align: center; }
.result-success { background: #f0fff4; }
.result-fail    { background: #fff5f5; }
.result-cancel  { background: #f7f7f7; }
.result-box p   { font-size: 16px; font-weight: 700; margin-bottom: 16px; }
.fail-desc      { font-size: 13px; color: #888; margin-top: -10px; }

.result-info {
  background: #fff; border-radius: 10px; padding: 14px 16px; margin-bottom: 16px; text-align: left;
}
.ri-row {
  display: flex; justify-content: space-between; padding: 5px 0;
  font-size: 13px; color: #555; border-bottom: 1px solid #f0f0f0;
}
.ri-row:last-child { border-bottom: none; }
.ri-row strong { color: #1a1a2e; }

.btn-cancel {
  width: 100%; padding: 11px; background: #fff5f5; color: #c53030;
  border: 1px solid #fed7d7; border-radius: 8px;
  font-size: 14px; font-weight: 700; cursor: pointer; transition: all 0.2s; margin-bottom: 8px;
}
.btn-cancel:hover { background: #fed7d7; }

@keyframes spin { to { transform: rotate(360deg); } }
</style>